<template>
  <section class="agentcore-page">
    <div class="page-header">
      <p class="eyebrow">Phase 4 Runtime</p>
      <h2>AgentCore Admin</h2>
      <p>
        Inspect the AWS-hosted Spyglass AI DOS AgentCore runtime and Gateway,
        run the deterministic candidate-generation POC, and perform targeted
        Gateway checks. Falcon owns AWS credentials and audit persistence.
      </p>
    </div>

    <TabView class="agentcore-tabs">
      <TabPanel header="POC Run">
        <section class="tab-section wide-tab">
          <div class="tab-copy">
            <h3>Deterministic AgentCore POC</h3>
            <p>
              Generate catalog candidates for one saved scenario, verify every
              package with the sizing engine, and retain durable audit
              correlation.
            </p>
          </div>
          <label>
            Scenario ID
            <InputText
              v-model="pocScenarioId"
              placeholder="saved scenario uuid"
            />
          </label>
          <label>
            Minimum Candidates
            <InputText v-model="pocMinimumCandidates" />
          </label>
          <Button
            label="Run POC"
            class="p-button-success"
            :loading="loading.poc"
            :disabled="!pocScenarioId.trim()"
            @click="runPoc"
          />
        </section>
      </TabPanel>

      <TabPanel header="Status">
        <section class="tab-section">
          <div class="tab-copy">
            <h3>Runtime &amp; Gateway Status</h3>
            <p>
              Confirm the AWS AgentCore runtime, Gateway, and Gateway target are
              reachable from Falcon with server-side credentials.
            </p>
          </div>
          <Button
            label="Load Status"
            class="p-button-success"
            :loading="loading.status"
            @click="loadStatus"
          />
        </section>
      </TabPanel>

      <TabPanel header="Audit Log">
        <section class="audit-tab">
          <div class="audit-heading">
            <div class="tab-copy">
              <h3>Agent Run Audit</h3>
              <p>
                Review AgentCore runs and inspect their Gateway tool calls,
                calculation traces, failures, and correlation identifiers.
              </p>
            </div>
            <Button
              v-tooltip="'Refresh audit log'"
              icon="pi pi-refresh"
              aria-label="Refresh audit log"
              class="p-button-secondary"
              :loading="loading.auditList"
              @click="loadAuditFirstPage"
            />
          </div>

          <div class="audit-filters">
            <label>
              Status
              <Dropdown
                v-model="auditStatus"
                :options="auditStatusOptions"
                option-label="label"
                option-value="value"
                @change="loadAuditFirstPage"
              />
            </label>
            <label>
              Scenario ID
              <InputText
                v-model="auditScenarioId"
                placeholder="filter by scenario uuid"
                @keyup.enter="loadAuditFirstPage"
              />
            </label>
            <label>
              Agent Run ID
              <span class="lookup-control">
                <InputText
                  v-model="auditRunId"
                  placeholder="open a specific run uuid"
                  @keyup.enter="loadAudit()"
                />
                <Button
                  v-tooltip="'Open run'"
                  icon="pi pi-search"
                  aria-label="Open run"
                  :loading="loading.audit"
                  :disabled="!auditRunId.trim()"
                  @click="loadAudit()"
                />
              </span>
            </label>
          </div>

          <DataTable
            class="audit-table p-datatable-sm"
            data-key="agentRunId"
            :value="auditRuns"
            :loading="loading.auditList"
            :lazy="true"
            :paginator="true"
            :rows="auditPageSize"
            :first="auditOffset"
            :total-records="auditTotal"
            :rows-per-page-options="[10, 25, 50]"
            responsive-layout="scroll"
            @page="onAuditPage"
          >
            <template #empty>
              <div class="empty-state">
                No AgentCore runs match these filters.
              </div>
            </template>
            <Column header="Started">
              <template #body="{ data }">
                <div class="stacked-cell">
                  <strong>{{ formatTimestamp(data.startedAt) }}</strong>
                  <span>{{ data.agentVersion }}</span>
                </div>
              </template>
            </Column>
            <Column header="Status">
              <template #body="{ data }">
                <Tag
                  :value="labelFor(data.status)"
                  :severity="auditStatusSeverity(data.status)"
                />
              </template>
            </Column>
            <Column header="Scenario">
              <template #body="{ data }">
                <code :title="data.scenarioId">
                  {{ shortId(data.scenarioId) }}
                </code>
              </template>
            </Column>
            <Column header="Outcome">
              <template #body="{ data }">
                <div class="stacked-cell">
                  <strong v-if="data.errorCode" class="failure-text">
                    {{ data.errorCode }}
                  </strong>
                  <strong v-else>{{ candidateSummary(data) }}</strong>
                  <span>
                    {{ data.toolCallCount }} tool call(s),
                    {{ data.calculationTraceCount }} trace(s)
                  </span>
                </div>
              </template>
            </Column>
            <Column header="Duration">
              <template #body="{ data }">
                {{ formatDuration(data.elapsedMs) }}
              </template>
            </Column>
            <Column header="Run ID">
              <template #body="{ data }">
                <code :title="data.agentRunId">
                  {{ shortId(data.agentRunId) }}
                </code>
              </template>
            </Column>
            <Column header="Actions">
              <template #body="{ data }">
                <Button
                  v-tooltip="'Inspect run'"
                  icon="pi pi-eye"
                  aria-label="Inspect run"
                  class="p-button-text"
                  :loading="
                    loading.audit && loadingAuditRunId === data.agentRunId
                  "
                  @click="loadAudit(data.agentRunId)"
                />
              </template>
            </Column>
          </DataTable>

          <Panel
            v-if="selectedAudit"
            :header="`Run ${shortId(selectedAudit.agentRunId)}`"
            class="audit-detail"
          >
            <div class="detail-actions">
              <Tag
                :value="labelFor(selectedAudit.status)"
                :severity="auditStatusSeverity(selectedAudit.status)"
              />
              <Button
                v-tooltip="'Copy audit JSON'"
                icon="pi pi-copy"
                aria-label="Copy audit JSON"
                class="p-button-text"
                @click="copyJson(selectedAudit)"
              />
            </div>

            <div class="audit-summary">
              <div>
                <span>Run ID</span>
                <code>{{ selectedAudit.agentRunId }}</code>
              </div>
              <div>
                <span>Started</span>
                <strong>{{ formatTimestamp(selectedAudit.startedAt) }}</strong>
              </div>
              <div>
                <span>Duration</span>
                <strong>{{ formatDuration(selectedAudit.elapsedMs) }}</strong>
              </div>
              <div>
                <span>Scenario</span>
                <code>{{ selectedAudit.scenarioId }}</code>
              </div>
              <div>
                <span>Runtime session</span>
                <code>{{ selectedAudit.runtimeSessionId }}</code>
              </div>
              <div>
                <span>Contract</span>
                <code>{{ selectedAudit.contractVersion }}</code>
              </div>
              <div>
                <span>Mode</span>
                <strong>{{ selectedAudit.calculationMode }}</strong>
              </div>
              <div>
                <span>Agent</span>
                <strong>{{ selectedAudit.agentVersion }}</strong>
              </div>
              <div>
                <span>Actor</span>
                <code>{{ selectedAudit.actorId || "Not recorded" }}</code>
              </div>
            </div>

            <div v-if="selectedAudit.errorCode" class="audit-failure">
              <strong>{{ selectedAudit.errorCode }}</strong>
              <span>{{ selectedAudit.errorMessage }}</span>
            </div>

            <section class="detail-section">
              <h4>Tool Calls</h4>
              <DataTable
                class="p-datatable-sm"
                :value="selectedAudit.toolCalls"
                responsive-layout="scroll"
              >
                <template #empty>No tool calls were recorded.</template>
                <Column header="Tool">
                  <template #body="{ data }">
                    <div class="stacked-cell">
                      <strong>{{ shortToolName(data.toolName) }}</strong>
                      <span>{{ data.targetName }}</span>
                    </div>
                  </template>
                </Column>
                <Column header="Status">
                  <template #body="{ data }">
                    <Tag
                      :value="labelFor(data.status)"
                      :severity="auditStatusSeverity(data.status)"
                    />
                  </template>
                </Column>
                <Column header="Duration">
                  <template #body="{ data }">
                    {{ formatDuration(data.elapsedMs) }}
                  </template>
                </Column>
                <Column header="Calculation Trace">
                  <template #body="{ data }">
                    <code v-if="data.calculationTraceId">
                      {{ data.calculationTraceId }}
                    </code>
                    <span v-else>None</span>
                  </template>
                </Column>
                <Column header="Error">
                  <template #body="{ data }">
                    <span :class="{ 'failure-text': data.errorCode }">
                      {{ data.errorCode || "None" }}
                    </span>
                  </template>
                </Column>
              </DataTable>
            </section>

            <section class="detail-section">
              <h4>Calculation Traces</h4>
              <DataTable
                class="p-datatable-sm"
                :value="selectedAudit.calculationTraces"
                responsive-layout="scroll"
              >
                <template #empty>No calculation traces were recorded.</template>
                <Column header="Trace ID">
                  <template #body="{ data }">
                    <code>{{ data.traceId }}</code>
                  </template>
                </Column>
                <Column header="Status">
                  <template #body="{ data }">
                    <Tag
                      :value="labelFor(data.status)"
                      :severity="traceStatusSeverity(data.status)"
                    />
                  </template>
                </Column>
                <Column header="Engine">
                  <template #body="{ data }">
                    <div class="stacked-cell">
                      <strong>{{ data.engineVersion }}</strong>
                      <code :title="data.engineHash">
                        {{ shortHash(data.engineHash) }}
                      </code>
                    </div>
                  </template>
                </Column>
                <Column header="Duration">
                  <template #body="{ data }">
                    {{ formatDuration(data.elapsedMs) }}
                  </template>
                </Column>
                <Column header="Created">
                  <template #body="{ data }">
                    {{ formatTimestamp(data.createdAt) }}
                  </template>
                </Column>
              </DataTable>
            </section>

            <details class="raw-audit">
              <summary>Response metadata and hashes</summary>
              <AdminJsonViewer :value="selectedAudit" />
            </details>
          </Panel>
        </section>
      </TabPanel>

      <TabPanel header="Runtime Invoke">
        <section class="tab-section wide-tab">
          <div class="tab-copy">
            <h3>Runtime Invoke</h3>
            <p>
              Invoke the deterministic AWS-hosted runtime directly without
              creating the complete Falcon-side audit lifecycle.
            </p>
          </div>
          <label>
            Payload JSON
            <textarea v-model="runtimePayloadJson" rows="8" />
          </label>
          <Button
            label="Invoke Runtime"
            class="p-button-success"
            :loading="loading.invoke"
            @click="invokeRuntime"
          />
        </section>
      </TabPanel>

      <TabPanel header="MCP Tools">
        <section class="tab-section">
          <div class="tab-copy">
            <h3>MCP Tools</h3>
            <p>
              List the tools currently exposed by the AgentCore Gateway. This
              does not call Spyglass; it only validates Gateway/MCP auth and
              schema registration.
            </p>
          </div>
          <Button
            label="List Tools"
            class="p-button-success"
            :loading="loading.tools"
            @click="listTools"
          />
        </section>
      </TabPanel>

      <TabPanel header="Frontend Source">
        <section class="tab-section wide-tab">
          <div class="tab-copy">
            <h3>Frontend Recommendation Source</h3>
            <p>
              Choose the AgentCore Gateway target Falcon should use when the
              normal <code>Generate Design</code> UI calls
              <code>/api/v1/ai-equipment-suggestions</code>. This setting is
              server-side; regular users never see MCP tool names or target
              prefixes.
            </p>
          </div>
          <div class="form-grid">
            <label>
              Source / Target Prefix
              <Dropdown
                v-model="frontendSource"
                :options="frontendSourceOptions"
                option-label="label"
                option-value="value"
                editable
              />
              <small>
                Examples: <code>stg</code>, <code>local-adam</code>, or
                <code>spyglass-falcon-tools-local-adam</code>.
              </small>
            </label>
            <label>
              Minimum Candidates
              <InputText v-model="frontendMinimumCandidates" />
              <small>Falcon clamps this value to 1-25.</small>
            </label>
          </div>
          <div class="button-row">
            <Button
              label="Load Config"
              class="p-button-secondary"
              :loading="loading.recommendationConfig"
              @click="loadRecommendationConfig"
            />
            <Button
              label="Save Source"
              class="p-button-success"
              :loading="loading.saveRecommendationConfig"
              @click="saveRecommendationConfig"
            />
          </div>
          <p class="muted-copy">
            This in-process override is intended for admin/dev routing tests.
            Salt/env config remains the durable deploy-time default.
          </p>
        </section>
      </TabPanel>

      <TabPanel header="Tool Call">
        <section class="tab-section wide-tab">
          <div class="tab-copy">
            <h3>Tool Call</h3>
            <p>
              Run a targeted MCP <code>tools/call</code>. These calls reach the
              configured Spyglass environment through the Gateway target.
            </p>
          </div>
          <div class="form-grid">
            <label>
              Target
              <Dropdown
                v-model="selectedTargetPrefix"
                :options="targetOptions"
                option-label="label"
                option-value="value"
                @change="syncTargetTool"
              />
              <small>
                Calls go to <code>{{ selectedTargetPrefix }}</code> through the
                AgentCore Gateway.
              </small>
            </label>

            <label>
              Tool
              <Dropdown
                v-model="toolName"
                :options="toolOptions"
                option-label="label"
                option-value="value"
                editable
                @change="handleToolChange"
              />
              <small v-if="selectedToolTechnicalName">
                MCP name: <code>{{ selectedToolTechnicalName }}</code>
              </small>
            </label>

            <template v-if="helperFields.length">
              <label v-for="field in helperFields" :key="field.name">
                {{ field.label }}
                <Dropdown
                  v-if="field.options?.length"
                  v-model="toolFieldValues[field.name]"
                  :options="field.options"
                  editable
                  @change="syncToolArguments"
                />
                <InputText
                  v-else
                  v-model="toolFieldValues[field.name]"
                  :placeholder="field.placeholder"
                  @input="syncToolArguments"
                />
                <small v-if="field.description">{{ field.description }}</small>
              </label>
            </template>

            <label>
              Arguments JSON
              <textarea v-model="toolArgumentsJson" rows="10" />
            </label>
          </div>
          <Button
            label="Call Tool"
            class="p-button-success"
            :loading="loading.callTool"
            @click="callTool"
          />
          <p class="muted-copy">
            Tool calls require the target Spyglass environment to have AI DOS
            flags and <code>AI_DOS_ACTION_SECRET</code> deployed.
          </p>
          <p v-if="selectedToolKind === 'scenario'" class="muted-copy">
            Yes: this selected tool needs a scenario UUID. It does not accept a
            sizing UUID directly. The UUID must exist in the selected Gateway
            target environment{{ selectedToolTargetLabel }}.
          </p>
          <p v-else-if="selectedToolKind === 'custom'" class="muted-copy">
            This tool is not one of the built-in presets. Helper fields are
            generated from the MCP schema when possible; edit Arguments JSON for
            nested objects or advanced payloads.
          </p>
        </section>
      </TabPanel>
    </TabView>

    <div v-if="error" class="error-banner">{{ error }}</div>

    <Card v-if="statusResult" class="mt-4">
      <template #title>Current Status</template>
      <template #content>
        <div class="status-grid">
          <div>
            <strong>Runtime:</strong>
            {{ nested(statusResult, "runtime.name") }}
            <Tag
              :value="
                String(nested(statusResult, 'runtime.status') || 'unknown')
              "
              :severity="
                statusTag(String(nested(statusResult, 'runtime.status') || ''))
              "
            />
          </div>
          <div>
            <strong>Gateway:</strong>
            {{ nested(statusResult, "gateway.name") }}
            <Tag
              :value="
                String(nested(statusResult, 'gateway.status') || 'unknown')
              "
              :severity="
                statusTag(String(nested(statusResult, 'gateway.status') || ''))
              "
            />
          </div>
          <div><strong>Region:</strong> {{ statusResult.region }}</div>
          <div>
            <strong>Runtime version:</strong>
            {{ nested(statusResult, "runtime.version") }}
          </div>
          <div>
            <strong>Model:</strong>
            {{
              nested(
                statusResult,
                "runtime.environmentVariables.AI_DOS_MODEL_ID"
              )
            }}
          </div>
          <div>
            <strong>Gateway URL:</strong>
            <code>{{ nested(statusResult, "gateway.url") }}</code>
          </div>
        </div>
      </template>
    </Card>

    <Panel v-if="latestResult" :header="latestHeader" class="mt-4">
      <div class="artifact-actions">
        <Button
          label="Copy JSON"
          class="p-button-text"
          @click="copyJson(latestResult)"
        />
      </div>
      <AdminJsonViewer :value="latestResult" />
    </Panel>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import type { DataTablePageEvent } from "primevue/datatable";
import { useToast } from "primevue/usetoast";

import {
  runAgentCoreAdminOperation,
  type AgentCoreAdminResponse,
  type AgentCoreAuditListResponse,
  type AgentCoreAuditRecord,
  type AgentCoreAuditRunSummary,
  type AgentCoreAuditStatus
} from "@/libs/aiDos/adminApi";
import { copyJsonToClipboard, errorMessage } from "@/libs/aiDos/adminShared";
import AdminJsonViewer from "./AdminJsonViewer.vue";

const toast = useToast();
const error = ref<string | null>(null);
const statusResult = ref<AgentCoreAdminResponse | null>(null);
const toolsResult = ref<AgentCoreAdminResponse | null>(null);
const latestResult = ref<AgentCoreAdminResponse | null>(null);
const latestHeader = ref("AgentCore Result");
const loading = ref({
  status: false,
  poc: false,
  audit: false,
  auditList: false,
  invoke: false,
  tools: false,
  callTool: false,
  recommendationConfig: false,
  saveRecommendationConfig: false
});
const runtimePayloadJson = ref(
  JSON.stringify({ scenarioId: "", minimumCandidates: 3 }, null, 2)
);
const pocScenarioId = ref("");
const pocMinimumCandidates = ref("3");
const auditRunId = ref("");
const auditScenarioId = ref("");
const auditStatus = ref<AgentCoreAuditStatus | "">("");
const auditRuns = ref<AgentCoreAuditRunSummary[]>([]);
const auditTotal = ref(0);
const auditOffset = ref(0);
const auditPageSize = ref(25);
const selectedAudit = ref<AgentCoreAuditRecord | null>(null);
const loadingAuditRunId = ref("");
const auditStatusOptions = [
  { label: "All statuses", value: "" },
  { label: "Completed", value: "completed" },
  { label: "Failed", value: "failed" },
  { label: "Running", value: "running" }
];
const DEFAULT_TARGET_PREFIX = "spyglass-falcon-tools-stg";
const DEFAULT_TOOL_PREFIX = `${DEFAULT_TARGET_PREFIX}___`;
const toolName = ref(
  `${DEFAULT_TOOL_PREFIX}generate_calculation_verified_candidates`
);
const selectedTargetPrefix = ref(DEFAULT_TARGET_PREFIX);
const toolFieldValues = ref<Record<string, string>>({
  scenarioId: "",
  equipmentType: "pump",
  search: ""
});
const equipmentTypes = [
  "pump",
  "pump_housing",
  "motor",
  "intake",
  "gas_separator",
  "seal",
  "sensor",
  "mle",
  "downhole_cable",
  "surface_cable",
  "vsd",
  "switchboard",
  "step_up_transformer",
  "step_down_transformer"
];
const toolArgumentsJson = ref("");
const frontendSource = ref("stg");
const frontendMinimumCandidates = ref("3");

const FRIENDLY_TOOL_LABELS: Record<string, string> = {
  get_sizing_report_context: "Load Sizing Report Context",
  generate_calculation_verified_candidates: "Generate Verified Candidates",
  run_sizing_calculation: "Run Sizing Calculation",
  search_spyglass_generic_materials: "Search Spyglass Materials"
};

const shortToolName = (name: string) => name.split("___").pop() || name;

const targetPrefix = (name: string) =>
  name.includes("___") ? name.split("___")[0] : "";

const toolTargetLabel = (name: string) => {
  const prefix = targetPrefix(name);
  if (prefix.endsWith("-stg")) return "stg";
  const localMatch = prefix.match(/local-(.+)$/);
  if (localMatch?.[1]) return `local ${localMatch[1]}`;
  return prefix || "custom";
};

const friendlyToolLabel = (name: string) => {
  const shortName = shortToolName(name);
  const label = FRIENDLY_TOOL_LABELS[shortName] || labelFor(shortName);
  return name.includes("___") ? `${label} (${toolTargetLabel(name)})` : label;
};

const availableToolNames = computed(() => {
  const tools = (
    toolsResult.value?.body as { result?: { tools?: Array<{ name: string }> } }
  )?.result?.tools;
  return (
    tools?.map(tool => tool.name) || [
      "spyglass-falcon-tools-stg___get_sizing_report_context",
      "spyglass-falcon-tools-stg___generate_calculation_verified_candidates",
      "spyglass-falcon-tools-stg___run_sizing_calculation",
      "spyglass-falcon-tools-stg___search_spyglass_generic_materials"
    ]
  );
});

const targetOptions = computed(() => {
  const prefixes = Array.from(
    new Set(availableToolNames.value.map(targetPrefix).filter(Boolean))
  );
  return prefixes.map(prefix => ({
    label: toolTargetLabel(`${prefix}___placeholder`),
    value: prefix
  }));
});

const frontendSourceOptions = computed(() => [
  { label: "stg", value: "stg" },
  ...targetOptions.value.map(option => ({
    label: `${option.label} (${option.value})`,
    value: option.value
  }))
]);

const toolOptions = computed(() => {
  const names = availableToolNames.value.filter(
    name => targetPrefix(name) === selectedTargetPrefix.value
  );
  return names.map(name => ({ label: friendlyToolLabel(name), value: name }));
});

type ToolSchema = {
  name: string;
  description?: string;
  inputSchema?: {
    required?: string[];
    properties?: Record<
      string,
      {
        type?: string;
        format?: string;
        description?: string;
        enum?: string[];
      }
    >;
  };
};

type HelperField = {
  name: string;
  label: string;
  placeholder?: string;
  description?: string;
  options?: string[];
};

const listedTools = computed<ToolSchema[]>(() => {
  const tools = (
    toolsResult.value?.body as { result?: { tools?: ToolSchema[] } }
  )?.result?.tools;
  return tools || [];
});

const selectedTool = computed(() =>
  listedTools.value.find(tool => tool.name === toolName.value)
);

const selectedToolTechnicalName = computed(() => toolName.value);

const selectedToolTargetLabel = computed(() => {
  if (!toolName.value.includes("___")) return "";
  return ` (${toolTargetLabel(toolName.value)})`;
});

const selectedToolKind = computed(() => {
  if (toolName.value.endsWith("get_sizing_report_context")) return "scenario";
  if (toolName.value.endsWith("generate_calculation_verified_candidates")) {
    return "scenario";
  }
  if (toolName.value.endsWith("run_sizing_calculation")) return "scenario";
  if (toolName.value.endsWith("search_spyglass_generic_materials")) {
    return "material-search";
  }
  return "custom";
});

const labelFor = (name: string) =>
  name
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/^\w/, value => value.toUpperCase());

const helperFields = computed<HelperField[]>(() => {
  const schema = selectedTool.value?.inputSchema;
  const properties = schema?.properties || {};
  const required = new Set(schema?.required || []);
  const fields = Object.entries(properties)
    .filter(([, property]) => {
      // Render direct helper fields for scalar-ish inputs. Nested objects stay
      // editable in Arguments JSON.
      const type = property.type || "string";
      return type !== "object" && type !== "array";
    })
    .map(([name, property]) => ({
      name,
      label: `${labelFor(name)}${required.has(name) ? " *" : ""}`,
      placeholder:
        property.format === "uuid"
          ? `${labelFor(name).toLowerCase()} uuid`
          : property.description || name,
      description: property.description,
      options:
        property.enum || (name === "equipmentType" ? equipmentTypes : undefined)
    }));
  if (!fields.length && selectedToolKind.value === "scenario") {
    return [
      {
        name: "scenarioId",
        label: "Scenario ID *",
        placeholder: "scenario uuid",
        description:
          "Required for context and calculation tools. Use the scenario UUID from a saved sizing route."
      }
    ];
  }
  return fields;
});

const toolArguments = () => {
  if (toolName.value.endsWith("get_sizing_report_context")) {
    return { scenarioId: (toolFieldValues.value.scenarioId || "").trim() };
  }
  if (toolName.value.endsWith("run_sizing_calculation")) {
    return {
      scenarioId: (toolFieldValues.value.scenarioId || "").trim(),
      calculationMode: "baseline_existing_equipment",
      debug: true,
      persistTrace: true
    };
  }
  if (toolName.value.endsWith("generate_calculation_verified_candidates")) {
    return {
      scenarioId: (toolFieldValues.value.scenarioId || "").trim(),
      minimumCandidates: 3
    };
  }
  if (toolName.value.endsWith("search_spyglass_generic_materials")) {
    const searchText = (toolFieldValues.value.search || "").trim();
    return {
      equipmentType: toolFieldValues.value.equipmentType || "pump",
      ...(searchText ? { search: searchText } : {}),
      filters: { manufacturers: ["Summit"] },
      limit: 5
    };
  }
  if (helperFields.value.length) {
    return helperFields.value.reduce<Record<string, string>>((args, field) => {
      const value = toolFieldValues.value[field.name];
      if (value !== undefined && value !== "") {
        args[field.name] = value;
      }
      return args;
    }, {});
  }
  return parseJson(toolArgumentsJson.value);
};

const syncToolArguments = () => {
  if (selectedToolKind.value !== "custom" || helperFields.value.length) {
    toolArgumentsJson.value = JSON.stringify(toolArguments(), null, 2);
  }
};

const syncTargetTool = () => {
  const shortName = shortToolName(toolName.value);
  const matchingTool = availableToolNames.value.find(
    name =>
      targetPrefix(name) === selectedTargetPrefix.value &&
      shortToolName(name) === shortName
  );
  const firstTargetTool = availableToolNames.value.find(
    name => targetPrefix(name) === selectedTargetPrefix.value
  );
  toolName.value = matchingTool || firstTargetTool || toolName.value;
  syncToolArguments();
};

const handleToolChange = () => {
  const prefix = targetPrefix(toolName.value);
  if (prefix) selectedTargetPrefix.value = prefix;
  syncToolArguments();
};

const refreshToolOptions = async () => {
  try {
    toolsResult.value = await runAgentCoreAdminOperation("list_tools");
    const prefixes = targetOptions.value.map(option => option.value);
    const localPrefix = prefixes.find(prefix => prefix.includes("-local-"));
    if (localPrefix && selectedTargetPrefix.value === DEFAULT_TARGET_PREFIX) {
      selectedTargetPrefix.value = localPrefix;
      syncTargetTool();
    } else if (!prefixes.includes(selectedTargetPrefix.value) && prefixes[0]) {
      selectedTargetPrefix.value = prefixes[0];
      syncTargetTool();
    }
  } catch {
    // Keep the static staging fallback if Gateway tools cannot be loaded yet.
  }
};

const parseJson = (value: string) => {
  try {
    return value.trim() ? JSON.parse(value) : {};
  } catch {
    throw new Error("JSON input is invalid.");
  }
};

const run = async (
  key: keyof typeof loading.value,
  header: string,
  operation: Parameters<typeof runAgentCoreAdminOperation>[0],
  payload: Record<string, unknown> = {}
) => {
  loading.value[key] = true;
  error.value = null;
  try {
    const result = await runAgentCoreAdminOperation(operation, payload);
    latestHeader.value = header;
    latestResult.value = result;
    if (operation === "status") {
      statusResult.value = result;
    }
    if (operation === "list_tools") {
      toolsResult.value = result;
    }
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    loading.value[key] = false;
  }
};

const loadStatus = () => run("status", "Status JSON", "status");
const runPoc = () =>
  run("poc", "Deterministic AgentCore POC Result", "invoke_calculation_poc", {
    scenarioId: pocScenarioId.value.trim(),
    mode: "candidate_generation",
    minimumCandidates: Number(pocMinimumCandidates.value || 3)
  });

const loadAuditRuns = async () => {
  loading.value.auditList = true;
  error.value = null;
  try {
    const result = await runAgentCoreAdminOperation<AgentCoreAuditListResponse>(
      "list_calculation_poc_audits",
      {
        status: auditStatus.value || undefined,
        scenarioId: auditScenarioId.value.trim() || undefined,
        limit: auditPageSize.value,
        offset: auditOffset.value
      }
    );
    auditRuns.value = result.runs;
    auditTotal.value = result.totalCount;
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    loading.value.auditList = false;
  }
};

const loadAuditFirstPage = () => {
  auditOffset.value = 0;
  return loadAuditRuns();
};

const onAuditPage = (event: DataTablePageEvent) => {
  auditPageSize.value = event.rows;
  auditOffset.value = event.first;
  loadAuditRuns();
};

const loadAudit = async (runId = auditRunId.value.trim()) => {
  const normalizedRunId = runId.trim();
  if (!normalizedRunId) return;
  loading.value.audit = true;
  loadingAuditRunId.value = normalizedRunId;
  error.value = null;
  try {
    selectedAudit.value =
      await runAgentCoreAdminOperation<AgentCoreAuditRecord>(
        "get_calculation_poc_audit",
        { agentRunId: normalizedRunId }
      );
    auditRunId.value = normalizedRunId;
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    loading.value.audit = false;
    loadingAuditRunId.value = "";
  }
};

const invokeRuntime = () =>
  run("invoke", "Runtime Invoke JSON", "invoke_runtime", {
    payload: parseJson(runtimePayloadJson.value)
  });
const listTools = () => run("tools", "MCP Tools JSON", "list_tools");
const callTool = () =>
  run("callTool", "Tool Call JSON", "call_tool", {
    toolName: toolName.value,
    arguments: toolArguments()
  });

const loadRecommendationConfig = async () => {
  loading.value.recommendationConfig = true;
  error.value = null;
  try {
    const result = await runAgentCoreAdminOperation("recommendation_config");
    latestHeader.value = "Frontend Source Config JSON";
    latestResult.value = result;
    frontendSource.value = String(
      result.source || result.targetPrefix || "stg"
    );
    frontendMinimumCandidates.value = String(result.minimumCandidates || 3);
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    loading.value.recommendationConfig = false;
  }
};

const saveRecommendationConfig = async () => {
  loading.value.saveRecommendationConfig = true;
  error.value = null;
  try {
    const result = await runAgentCoreAdminOperation(
      "update_recommendation_config",
      {
        source: frontendSource.value,
        minimumCandidates: Number(frontendMinimumCandidates.value || 3)
      }
    );
    latestHeader.value = "Updated Frontend Source Config JSON";
    latestResult.value = result;
    frontendSource.value = String(
      result.source || result.targetPrefix || "stg"
    );
    frontendMinimumCandidates.value = String(result.minimumCandidates || 3);
    toast.add({
      severity: "success",
      summary: "Source saved",
      detail: "Frontend recommendation source updated for this Falcon process.",
      life: 3500
    });
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    loading.value.saveRecommendationConfig = false;
  }
};

syncToolArguments();
onMounted(() => {
  refreshToolOptions();
  loadRecommendationConfig();
  loadAuditRuns();
});

const nested = (value: unknown, path: string) =>
  path.split(".").reduce<unknown>((current, part) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[part];
  }, value) as string | number | undefined;

const statusTag = (status: string) =>
  status === "READY" ? "success" : "warning";
const auditStatusSeverity = (status: AgentCoreAuditStatus) => {
  if (status === "completed") return "success";
  if (status === "failed") return "danger";
  return "info";
};
const traceStatusSeverity = (status: string) => {
  if (status === "pass") return "success";
  if (status === "warning") return "warning";
  return "danger";
};
const formatTimestamp = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "medium"
      }).format(new Date(value))
    : "Not recorded";
const formatDuration = (value?: number | null) =>
  value == null ? "In progress" : `${value.toLocaleString()} ms`;
const shortId = (value: string) =>
  value.length > 12 ? `${value.slice(0, 8)}...` : value;
const shortHash = (value: string) =>
  value.length > 16 ? `${value.slice(0, 12)}...` : value;
const candidateSummary = (run: AgentCoreAuditRunSummary) => {
  const count = run.responseMetadata.displayableCandidateCount;
  return count == null
    ? "Candidate count not recorded"
    : `${Number(count)} candidate(s)`;
};
const copyJson = (value: unknown) => copyJsonToClipboard(toast, value);
</script>

<style scoped lang="scss">
.page-header {
  max-width: 58rem;
  margin-bottom: 1rem;

  h2 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
  }

  p:last-child {
    color: var(--text-color-secondary);
    line-height: 1.5;
  }
}

.eyebrow {
  color: var(--red-500);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  margin: 0 0 0.25rem;
  text-transform: uppercase;
}

.form-grid,
.status-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(16rem, 1fr));
}

.agentcore-tabs {
  margin-top: 1.5rem;
}

.tab-section {
  display: grid;
  gap: 1rem;
  max-width: 48rem;
  padding: 0.5rem 0 0;
}

.wide-tab {
  max-width: 64rem;
}

.audit-tab {
  display: grid;
  gap: 1.25rem;
  padding-top: 0.5rem;
}

.audit-heading,
.detail-actions {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.audit-filters {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(10rem, 0.6fr) minmax(18rem, 1fr) minmax(
      20rem,
      1.2fr
    );
}

.lookup-control {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: minmax(0, 1fr) 2.75rem;

  .p-button {
    height: 2.75rem;
    width: 2.75rem;
  }
}

.audit-table {
  min-height: 18rem;
}

.stacked-cell {
  display: grid;
  gap: 0.2rem;

  span {
    color: var(--text-color-secondary);
    font-size: 0.8rem;
  }
}

.failure-text {
  color: var(--red-600);
}

.audit-detail {
  margin-top: 0.5rem;
}

.audit-summary {
  border-bottom: 1px solid var(--surface-300);
  border-top: 1px solid var(--surface-300);
  display: grid;
  gap: 0;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  margin: 0.75rem 0 1.25rem;

  > div {
    display: grid;
    gap: 0.35rem;
    min-width: 0;
    padding: 0.9rem 1rem 0.9rem 0;
  }

  span {
    color: var(--text-color-secondary);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
  }
}

.audit-failure {
  background: var(--red-50);
  border-left: 3px solid var(--red-500);
  color: var(--red-800);
  display: grid;
  gap: 0.25rem;
  margin-bottom: 1.25rem;
  padding: 0.8rem 1rem;
}

.detail-section {
  margin-top: 1.5rem;

  h4 {
    font-size: 1rem;
    margin: 0 0 0.65rem;
  }
}

.raw-audit {
  border-top: 1px solid var(--surface-300);
  margin-top: 1.5rem;
  padding-top: 1rem;

  summary {
    cursor: pointer;
    font-weight: 700;
    margin-bottom: 0.75rem;
  }
}

.empty-state {
  color: var(--text-color-secondary);
  padding: 2rem;
  text-align: center;
}

.tab-copy {
  h3 {
    font-size: 1.5rem;
    margin: 0 0 0.4rem;
  }

  p {
    color: var(--text-color-secondary);
    line-height: 1.45;
    margin: 0;
  }
}

label {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-weight: 700;
}

textarea {
  background: var(--surface-0);
  border: 1px solid var(--surface-400);
  border-radius: 0.375rem;
  color: var(--text-color);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  padding: 0.75rem;
}

.muted-copy {
  color: var(--text-color-secondary);
}

.error-banner {
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 0.5rem;
  color: var(--red-700);
  margin: 1rem 0;
  padding: 1rem;
}

.artifact-actions {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.button-row {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.json-output {
  background: var(--surface-900);
  border: 1px solid var(--surface-700);
  border-radius: 0.5rem;
  color: var(--surface-0);
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  font-size: 0.875rem;
  line-height: 1.45;
  max-height: 36rem;
  overflow: auto;
  padding: 1rem;
  white-space: pre;
}

code {
  word-break: break-all;
}

@media (max-width: 900px) {
  .audit-filters,
  .audit-summary {
    grid-template-columns: 1fr;
  }

  .audit-heading {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
